import { config } from "./config";
import type {
  AdminIdentity,
  AdminListResponse,
  AdminUser,
  AllowlistAddResult,
  AllowlistPage,
  AnalyticsPage,
  CohortsPage,
  FirstRequestsPage,
  RequestExamplesPage,
  DeliveryPage,
  EngagementPage,
  HandlePreview,
  LatencyPage,
  LiveSessionEventsPage,
  LiveSessionsPage,
  LiveSessionSummary,
  MessageKind,
  Role,
  TaskEnv,
  TaskRun,
  TaskRunsPage,
  UserIdentity,
  UserMessagesPage,
  UserTranslatePage,
  UserSessionState,
  WaitlistPage,
  WaitlistSubmission,
} from "./types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Called on any 401 so the app can drop the token and re-authenticate. */
let onUnauthorized: () => void = () => {};
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

let bearer: string | null = null;
/** The ID token lives in memory only — never localStorage. GIS auto_select
 *  re-issues one silently on reload, so persisting a bearer token would add
 *  XSS exposure for no real convenience. */
export function setToken(token: string | null) {
  bearer = token;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (bearer) headers.set("Authorization", `Bearer ${bearer}`);
  if (init.body) headers.set("Content-Type", "application/json");

  let res: Response;
  try {
    res = await fetch(`${config.engineBaseUrl}${path}`, { ...init, headers });
  } catch {
    // A CORS rejection is indistinguishable from an outage in the browser, and
    // a missing origin in BEAN_CORS_ORIGINS is the likeliest cause here.
    throw new ApiError(
      0,
      "Could not reach the engine. Check that it is running and that this " +
        "origin is listed in BEAN_CORS_ORIGINS.",
    );
  }

  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError(401, "Your session expired. Sign in again.");
  }

  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      /* non-JSON error body; keep the status line */
    }
    throw new ApiError(res.status, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface RegistrationRow {key:string;handle:string;account_id:string;created_at:number;first_inbound_at:number|null;consent_accepted:boolean;tier:string|null;origin:string;attribution:{fields:Record<string,string>}|null}
export interface RequestRow {key:string;kind:string;status:string;handle:string;email:string;email_verified:boolean;created_at:number;message?:string;resolution?:string;updated_by?:string}
export const api = {
  channelSignups: (cursor="") => request<{items:{key:string;email:string;created_at:number;consent:boolean}[];next_cursor:string|null}>("/admin/onboarding/channel-signups?cursor="+encodeURIComponent(cursor)),
  onboardingDiagnostics: (cursor="") => request<{items:{key:string;flow:string;updated_at:number;timeouts:number;errors:number;reason?:string;browser_family:string;platform:string;events:Record<string,{at:number;elapsed_ms:number}>}[];next_cursor:string|null}>("/admin/onboarding/diagnostics?cursor="+encodeURIComponent(cursor)),
  registrations: (cursor="") => request<{items:RegistrationRow[];next_cursor:string|null}>("/admin/onboarding/registrations?cursor="+encodeURIComponent(cursor)),
  requests: (cursor="") => request<{items:RequestRow[];next_cursor:string|null}>("/admin/onboarding/requests?cursor="+encodeURIComponent(cursor)),
  resolveRequest: (key:string,status:string,resolution:string) => request("/admin/onboarding/requests/"+encodeURIComponent(key), {method:"POST",body:JSON.stringify({status,resolution})}),
  me: () => request<AdminIdentity>("/admin/me"),

  listWaitlist: (cursor = "", limit = 100) =>
    request<WaitlistPage>(
      `/admin/waitlist?limit=${limit}` +
        (cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""),
    ),

  getWaitlist: (id: string) =>
    request<WaitlistSubmission>(
      `/admin/waitlist/${encodeURIComponent(id)}`,
    ),

  previewHandle: (id: string, raw: string) =>
    request<HandlePreview>(
      `/admin/waitlist/${encodeURIComponent(id)}/preview-handle`,
      { method: "POST", body: JSON.stringify({ raw }) },
    ),

  approve: (id: string, handle: string) =>
    request<{ id: string; status: string; handle: string }>(
      `/admin/waitlist/${encodeURIComponent(id)}/approve`,
      { method: "POST", body: JSON.stringify({ handle }) },
    ),

  reject: (id: string) =>
    request<{ id: string; status: string }>(
      `/admin/waitlist/${encodeURIComponent(id)}/reject`,
      { method: "POST" },
    ),

  listAllowlist: (cursor = "", limit = 200) =>
    request<AllowlistPage>(
      `/admin/allowlist?limit=${limit}` +
        (cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""),
    ),

  /** Resolve free text for the allowlist add form — no waitlist row involved. */
  previewAllowlistHandle: (raw: string) =>
    request<HandlePreview>("/admin/allowlist/preview-handle", {
      method: "POST",
      body: JSON.stringify({ raw }),
    }),

  addAllowlist: (handle: string, note = "") =>
    request<AllowlistAddResult>("/admin/allowlist", {
      method: "POST",
      body: JSON.stringify({ handle, note }),
    }),

  listAdmins: () => request<AdminListResponse>("/admin/admins"),

  addAdmin: (email: string, role: Role) =>
    request<AdminUser>("/admin/admins", {
      method: "POST",
      body: JSON.stringify({ email, role }),
    }),

  patchAdmin: (email: string, patch: { role?: Role; disabled?: boolean }) =>
    request<AdminUser>(`/admin/admins/${encodeURIComponent(email)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deleteAdmin: (email: string) =>
    request<{ email: string; deleted: boolean }>(
      `/admin/admins/${encodeURIComponent(email)}`,
      { method: "DELETE" },
    ),

  // ── Support: one user's conversation + cached session ──────────────
  // Read-only. Every call is logged server-side with the admin's identity and
  // the user opened — this is someone's private conversation.
  lookupUser: (q: string) =>
    request<UserIdentity>(`/admin/users/lookup?q=${encodeURIComponent(q)}`),

  userMessages: (userId: string, limit = 100) =>
    request<UserMessagesPage>(
      `/admin/users/${encodeURIComponent(userId)}/messages?limit=${limit}`,
    ),

  // One-off translation of some of the thread, by timestamp. Nothing is stored.
  // Same privacy footing as `userMessages`, logged with the admin.
  userTranslate: (userId: string, timestamps: string[], limit: number) =>
    request<UserTranslatePage>(
      `/admin/users/${encodeURIComponent(userId)}/translate`,
      { method: "POST", body: JSON.stringify({ timestamps, limit }) },
    ),

  userSession: (userId: string) =>
    request<UserSessionState>(
      `/admin/users/${encodeURIComponent(userId)}/session`,
    ),

  /** Who has actually written to Bean since `since` (YYYY-MM-DD), with their
   *  engagement counts. Aggregated server-side; see EngagementRow. */
  userEngagement: (since: string) =>
    request<EngagementPage>(
      `/admin/users/engagement?since=${encodeURIComponent(since)}`,
    ),

  // ── Analytics: the acquisition + engagement daily table ──────────
  /** Materialized daily rows. `until` defaults to today (PT) server-side. */
  analyticsDaily: (since: string, until = "", env = "") =>
    request<AnalyticsPage>(
      `/admin/analytics/daily?since=${encodeURIComponent(since)}` +
        (until ? `&until=${encodeURIComponent(until)}` : "") +
        (env ? `&env=${encodeURIComponent(env)}` : ""),
    ),

  // The real messages behind one category bar. Same privacy footing as
  // `userMessages`: these are people's own words, and every call is logged
  // server-side with the admin's identity and the category opened.
  analyticsRequests: (
    category: string,
    since: string,
    until = "",
    env = "",
    firstOnly = false,
  ) =>
    request<RequestExamplesPage>(
      `/admin/analytics/requests?category=${encodeURIComponent(category)}` +
        `&since=${encodeURIComponent(since)}` +
        (until ? `&until=${encodeURIComponent(until)}` : "") +
        (env ? `&env=${encodeURIComponent(env)}` : "") +
        (firstOnly ? "&first_only=true" : ""),
    ),

  // Every user's first ask + whether they stayed. Same privacy footing as
  // `userMessages`: people's own words, logged server-side with the admin.
  analyticsFirstRequests: (since: string, until = "", env = "") =>
    request<FirstRequestsPage>(
      `/admin/analytics/first-requests?since=${encodeURIComponent(since)}` +
        (until ? `&until=${encodeURIComponent(until)}` : "") +
        (env ? `&env=${encodeURIComponent(env)}` : ""),
    ),

  // Join-week cohorts. Computed on read: percentiles need per-user numbers,
  // which the daily table does not keep.
  analyticsCohorts: (since: string, until = "", env = "") =>
    request<CohortsPage>(
      `/admin/analytics/cohorts?since=${encodeURIComponent(since)}` +
        (until ? `&until=${encodeURIComponent(until)}` : "") +
        (env ? `&env=${encodeURIComponent(env)}` : ""),
    ),

  // ── Live session log: the durable per-meeting timeline ────────────
  listLiveSessions: (cursor = "", limit = 50) =>
    request<LiveSessionsPage>(
      `/admin/live-sessions?limit=${limit}` +
        (cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""),
    ),

  liveSession: (msid: string) =>
    request<LiveSessionSummary>(
      `/admin/live-sessions/${encodeURIComponent(msid)}`,
    ),

  /** `afterTs` fetches only what is new — the in-progress poll. The bound is
   *  inclusive (see the engine's `get_events`), so the caller must dedupe. */
  liveSessionEvents: (msid: string, afterTs = "") =>
    request<LiveSessionEventsPage>(
      `/admin/live-sessions/${encodeURIComponent(msid)}/events` +
        (afterTs ? `?after_ts=${encodeURIComponent(afterTs)}` : ""),
    ),

  // ── Task queue ───────────────────────────────────────────────────
  // `env` picks the Firestore collection, so the same engine serves both
  // stacks' queues. Read-only: retrying from here would double-run work the
  // queue is already about to redeliver.
  listTasks: (
    env: TaskEnv,
    { status = "", name = "", limit = 50 } = {},
  ) =>
    request<TaskRunsPage>(
      `/admin/tasks?env=${env}&limit=${limit}` +
        (status ? `&status=${encodeURIComponent(status)}` : "") +
        (name ? `&name=${encodeURIComponent(name)}` : ""),
    ),

  task: (taskId: string, env: TaskEnv) =>
    request<TaskRun & { env: TaskEnv }>(
      `/admin/tasks/${encodeURIComponent(taskId)}?env=${env}`,
    ),

  // ── Reply latency ────────────────────────────────────────────────
  // One row per inbound iMessage turn. `hours` must be 24, 168 or 720 — the
  // engine rejects anything else rather than clamping it.
  latency: (
    env: TaskEnv,
    {
      hours = 168,
      newUsers = true,
      cold = null as boolean | null,
      limit = 500,
    } = {},
  ) =>
    request<LatencyPage>(
      `/admin/latency?env=${env}&hours=${hours}&new_users=${newUsers}&limit=${limit}` +
        (cold === null ? "" : `&cold=${cold ? 1 : 0}`),
    ),

  // ── Message delivery ─────────────────────────────────────────────
  // One row per outbound text: Sendblue API acknowledgement → first DELIVERED
  // receipt. Same `hours` rule as `latency`; `kind` "" = all kinds.
  deliveries: (
    env: TaskEnv,
    { hours = 168, kind = "" as MessageKind | "", limit = 500 } = {},
  ) =>
    request<DeliveryPage>(
      `/admin/latency/deliveries?env=${env}&hours=${hours}&limit=${limit}` +
        (kind ? `&kind=${kind}` : ""),
    ),
};
